import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import { saveSaleData } from "../../slices/SaleSlice";
import { cartEventEffect } from "./cartEventEffect";
import { recommendationEffect } from "./recommendationEffect";
import { resetSaleEffect } from "./resetSaleEffect";
import { loyaltyRefundEffect } from "./loyaltyRefundEffect";
import { dotpeOfferReleaseEffect } from "./dotpeOfferReleaseEffect";
import { customerLoyaltyFetchEffect } from "./customerLoyaltyFetchEffect";
import { customerDisplayEffect } from "./customerDisplayEffect";
import { selectEngine } from "@rista/payments/engine/session";
import {
  askCustomerPhoneNumber,
  ispaymentInProcess,
  saleClosedOnDisplay,
  setSalePaymentDetails,
} from "../../slices/CustomerDisplay";

export * from "./shared";

/**
 * WEB variant of the sale-update middleware — the COMPOSER. See `./shared.ts` for why the two
 * platforms share no body.
 */

const saleUpdateMiddleware = createListenerMiddleware();

// Order matters: resetSaleEffect must run before cartEventEffect. Both read the order-placed
// marker (getLastOrderPlacedMid) to tell a discarded cart from one that was placed as an
// order, and cartEventEffect's handleCartDeleted clears that marker once it has used it.
const saleUpdateEffects = [
  resetSaleEffect,
  cartEventEffect,
  recommendationEffect,
  loyaltyRefundEffect,
  dotpeOfferReleaseEffect,
  customerLoyaltyFetchEffect,
];

/**
 * Attaches all sale update effects to the middleware listener.
 * Called once during store setup.
 */
export const setupSaleUpdateListener = () =>
  saleUpdateMiddleware.startListening({
    type: saveSaleData.type,
    effect: async (action, listenerApi: any) => {
      saleUpdateEffects.forEach((effect) => effect(action, listenerApi));
    },
  });

/**
 * Mirrors everything the display renders — the cart, and whether the payment
 * popup is up — onto it, from wherever the change was made.
 *
 * Deliberately NOT part of saleUpdateEffects above: that listener lives and
 * dies with the NewOrder screen, and plenty of saveSaleData dispatches happen
 * while it is unmounted — MyOrderCart's "Edit items" is the clearest one, which
 * loads the order and only then navigates to /new-order. Scoped that way the
 * push never fired for it and the display sat on the previous (usually empty)
 * cart until the customer's next item edit. Subscribed from
 * useCustomerDisplayHost, which is mounted for the life of the POS window.
 *
 * ispaymentInProcess has to be in here too, not just setSalePaymentDetails:
 * closing the payment popup without recording anything toggles only that flag,
 * and with nothing listening for it the display never heard that the payment
 * was over and kept showing "payment in progress" instead of the cart.
 *
 * saleClosedOnDisplay likewise: checkout success clears the cart in the same tick, so the
 * display has to be told the sale closed BEFORE the empty-cart push lands — otherwise the
 * only thing it ever sees is the idle screen.
 */
const displayActions = isAnyOf(saveSaleData, ispaymentInProcess, setSalePaymentDetails, saleClosedOnDisplay);

/**
 * ...and the payment engine's legs, which ARE the display's payments while an engine session is
 * open (`selectPaymentDetails`). Compared by identity: the engine's reducer is immer, so the array
 * changes only when a leg does — the pad's keystrokes do not push.
 */
export const setupCustomerDisplayListener = () =>
  saleUpdateMiddleware.startListening({
    predicate: (action, current: any, previous: any) =>
      displayActions(action) || selectEngine(current)?.legs !== selectEngine(previous)?.legs,
    effect: async (action, listenerApi: any) => {
      customerDisplayEffect(action, listenerApi);
    },
  });

// The Ask/Cancel toggle in CustomerSearchInput only updates the POS window's
// own store — nothing else re-pushes the cart to the display, so without this
// listener the second screen never hears about it. Kept separate from
// saveSaleData above: this needs to fire on askCustomerPhoneNumber alone, not
// on every sale save.
export const setupAskCustomerPhoneNumberListener = () =>
  saleUpdateMiddleware.startListening({
    actionCreator: askCustomerPhoneNumber,
    effect: async (action, listenerApi: any) => {
      customerDisplayEffect(action, listenerApi);
    },
  });

export default saleUpdateMiddleware;
